import {
  CLIENT_DOCUMENT_CATEGORY_LABELS,
  type ClientDocumentCategory,
  fileExtensionOf,
} from '@fos/shared/contracts/knowledge';

/** Etykieta rodzaju w liczbie mnogiej (filtr listy plików). */
export const CATEGORY_FILTER_LABELS: Record<ClientDocumentCategory, string> = {
  contract: 'Umowy',
  offer: 'Oferty',
  client_materials: 'Materiały od klienta',
  other: 'Inne',
};

export function categoryLabel(category: ClientDocumentCategory | null): string {
  return CLIENT_DOCUMENT_CATEGORY_LABELS[category ?? 'other'];
}

/**
 * Rodzaj podpowiadany przy wgraniu po nazwie pliku („Umowa_REA.pdf” → Umowa, „oferta-v2.pdf” → Oferta); bez trafienia
 * albo bez uprawnienia do podpowiedzianego rodzaju — „Inne” (albo pierwszy dozwolony).
 */
export function guessCategory(filenames: readonly string[], allowed: readonly ClientDocumentCategory[]) {
  const names = filenames.map((n) => n.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''));
  const pick = (c: ClientDocumentCategory) => (allowed.includes(c) ? c : null);
  const all = (re: RegExp) => names.length > 0 && names.every((n) => re.test(n));
  const guessed =
    (all(/umow|aneks|contract|nda/) && pick('contract')) ||
    (all(/ofert|wycen|proposal|quote/) && pick('offer')) ||
    (all(/brief|logo|material|zdjec|foto|photo|tresc/) && pick('client_materials')) ||
    null;
  return guessed ?? pick('other') ?? allowed[0] ?? 'other';
}

/** Domena adresu bez „www.” („https://www.figma.com/file/…” → „figma.com”); niepoprawny adres — sam tekst. */
export function linkDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Adres z pola edycji: bez protokołu dopisujemy https:// („figma.com/x” → „https://figma.com/x”). */
export function normalizeUrl(raw: string): string {
  const v = raw.trim();
  if (!v) return v;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`;
}

/**
 * Adres linku do sprawdzenia przed zapisem — bez zależności od parsera URL przeglądarki (Chrome przyjmuje spację
 * w hoście: „https://to nie jest adres” → „https://to%20nie%20jest%20adres/”, a serwer go odrzuca): http(s), bez
 * białych znaków, host z kropką (domena albo adres IP).
 */
export function isPlausibleUrl(url: string): boolean {
  return !/\s/.test(url) && /^https?:\/\/[^/?#@\s]+\.[^/?#\s]+/i.test(url);
}

/** Rozmiar pliku („512 B”, „120 KB”, „2,4 MB”). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** Rodzaj ikony pliku po rozszerzeniu. */
export function fileKind(filename: string): 'image' | 'sheet' | 'archive' | 'text' {
  const ext = fileExtensionOf(filename);
  if (ext && ['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return 'image';
  if (ext && ['xlsx', 'csv'].includes(ext)) return 'sheet';
  if (ext === 'zip') return 'archive';
  return 'text';
}
